import type { AuthResult } from '@moods/shared';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../store/auth';
import i18n from '../../i18n';

type SessionResult = Extract<AuthResult, { kind: 'session' }>;

export function acceptSession(r: SessionResult): void {
  useAuth.getState().setSession(r);
}

/** Where to go after signing in: the saved route, then onboarding for new accounts, then /home. */
export function postLoginPath(next?: string | null): string {
  const saved = next ?? useAuth.getState().expiredFrom;
  if (saved && saved.startsWith('/') && !saved.startsWith('//') && !saved.startsWith('/login')) return saved;
  return '/home';
}

/** Human message for an API error (field errors are handled by forms). */
/** ACCOUNT_SUSPENDED (Yönetim paneli): until when and why, in the reader's language. */
function suspendedMessage(d: { until?: string | null; reason?: string | null } | undefined): string {
  const until = d?.until ? new Date(d.until).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'tr-TR', { dateStyle: 'long', timeStyle: 'short' }) : null;
  const base = until ? i18n.t('errors.accountSuspendedUntil', { until }) : i18n.t('errors.accountSuspended');
  return d?.reason ? `${base} ${i18n.t('errors.suspensionReason', { reason: d.reason })}` : base;
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'NETWORK') return i18n.t('errors.network');
    if ((err.code === 'RATE_LIMITED' || err.code === 'ACCOUNT_LOCKED') && err.retryAfter) {
      return err.code === 'ACCOUNT_LOCKED'
        ? i18n.t('errors.accountLocked', { seconds: Math.ceil(err.retryAfter) })
        : i18n.t('errors.rateLimited', { seconds: Math.ceil(err.retryAfter) });
    }
    if (err.code === 'ACCOUNT_SUSPENDED') return suspendedMessage(err.details as { until?: string | null; reason?: string | null } | undefined);
    if (err.code === 'REGISTRATION_CLOSED') {
      const m = (err.details as { message?: { tr: string; en: string } } | undefined)?.message;
      return (m && (i18n.language === 'en' ? m.en : m.tr)) || i18n.t('errors.registrationClosed');
    }
    return err.message;
  }
  return i18n.t('errors.generic');
}

export const fieldOf = (err: unknown): string | undefined =>
  err instanceof ApiError ? ((err.details as { field?: string } | undefined)?.field ?? undefined) : undefined;
